import React from 'react';
import { useScrollAnimation, useStaggerAnimation } from '../../../hooks/useScrollAnimation';

const itens = [
  {
    icon: 'ri-search-eye-line',
    title: 'Diagnóstico técnico completo',
    description:
      'Mapeamos todos os riscos operacionais, patrimoniais e contratuais da sua empresa.',
  },
  {
    icon: 'ri-layout-masonry-line',
    title: 'Estruturação inteligente de coberturas',
    description:
      'Proteção alinhada com o risco real, sem lacunas e sem desperdícios.',
  },
  {
    icon: 'ri-shake-hands-line',
    title: 'Negociação profissional com seguradoras',
    description:
      'Garantimos melhores condições, menor custo e mais eficiência.',
  },
  {
    icon: 'ri-scales-3-line',
    title: 'Segurança jurídica e contratual',
    description:
      'Eliminamos falhas, cláusulas abusivas e riscos ocultos.',
  },
  {
    icon: 'ri-line-chart-line',
    title: 'Gestão contínua e monitoramento',
    description:
      'Sua empresa protegida hoje e sempre, com acompanhamento constante.',
  },
];

export default function ImpactoIBXBrasil() {
  const { ref: badgeRef, isVisible: badgeVisible } = useScrollAnimation();
  const { ref: listRef, visibleItems } = useStaggerAnimation(itens.length);
  const { ref: quoteRef, isVisible: quoteVisible } = useScrollAnimation();
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation();

  return (
    <section className="relative bg-gradient-to-br from-emerald-50/70 via-white to-teal-50/40 py-12 sm:py-16 lg:py-28 overflow-hidden">

      {/* Background subtle grid */}
      <div className="absolute inset-0 opacity-[0.025]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(rgba(0,0,0,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.04) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      {/* Soft accent glows */}
      <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-emerald-100/60 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-teal-100/50 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-5 sm:px-6 lg:px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-16 items-center min-h-[500px] lg:min-h-[700px]">

          {/* ── Coluna Esquerda — Conteúdo ── */}
          <div className="space-y-6 sm:space-y-8">

            {/* Badge + Título + Subtítulo */}
            <div
              ref={badgeRef as React.RefObject<HTMLDivElement>}
              className={`transition-all duration-700 ease-out ${
                badgeVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <div className="inline-flex items-center gap-2 sm:gap-2.5 bg-emerald-50 border border-emerald-200 rounded-full px-4 sm:px-5 py-2 mb-5 sm:mb-6">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse shrink-0" />
                <span className="text-emerald-700 font-bold text-xs sm:text-sm tracking-[0.15em] uppercase">
                  IBX Brasil
                </span>
              </div>

              <h2
                className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-slate-900 leading-[1.15] mb-4 sm:mb-5"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Com a IBX Brasil, sua empresa deixa de correr riscos e passa a operar com{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">
                  estratégia
                </span>
              </h2>

              <p className="text-slate-600 text-base sm:text-lg lg:text-xl leading-relaxed">
                Transformamos contratos de seguro em ferramentas reais de proteção e economia.
              </p>
            </div>

            {/* Lista de itens */}
            <div ref={listRef as React.RefObject<HTMLDivElement>} className="space-y-3 sm:space-y-4">
              {itens.map((item, index) => (
                <div
                  key={index}
                  className={`group flex items-start gap-3 sm:gap-4 bg-white border border-slate-200 rounded-lg sm:rounded-xl p-4 sm:p-5 hover:border-emerald-300 hover:bg-emerald-50/30 transition-all duration-700 ease-out ${
                    visibleItems[index] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                  }`}
                >
                  <div className="flex-shrink-0 w-9 h-9 sm:w-11 sm:h-11 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center justify-center group-hover:border-emerald-400 group-hover:bg-emerald-100 transition-colors">
                    <i className={`${item.icon} text-emerald-600 text-lg sm:text-xl`} />
                  </div>
                  <div className="flex-1 pt-0.5">
                    <h3 className="text-slate-900 font-bold text-sm sm:text-base lg:text-lg mb-1 leading-snug">
                      {item.title}
                    </h3>
                    <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                  <div className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity hidden sm:flex items-center pt-1">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-check-line text-emerald-500 text-lg" />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Frase de Impacto */}
            <div
              ref={quoteRef as React.RefObject<HTMLDivElement>}
              className={`relative bg-slate-900 rounded-xl sm:rounded-2xl p-5 sm:p-6 transition-all duration-700 ease-out ${
                quoteVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'
              }`}
            >
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-14 sm:h-16 bg-gradient-to-b from-emerald-500 to-teal-500 rounded-r" />
              <div className="flex items-start gap-2.5 sm:gap-3 pl-3">
                <i className="ri-double-quotes-l text-emerald-400/25 text-2xl sm:text-3xl flex-shrink-0" />
                <p className="text-white text-base sm:text-lg lg:text-xl font-bold leading-relaxed">
                  Com a assessoria certa, o seguro deixa de ser custo —{' '}
                  <span className="text-emerald-300">e passa a ser estratégia.</span>
                </p>
              </div>
            </div>

            {/* CTA */}
            <div
              ref={ctaRef as React.RefObject<HTMLDivElement>}
              className={`pt-2 sm:pt-4 transition-all duration-700 ease-out ${
                ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <a
                href="#analise-gratuita"
                className="group flex sm:inline-flex items-center justify-center gap-2 sm:gap-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold px-6 sm:px-8 py-4 sm:py-5 rounded-xl transition-all duration-300 w-full sm:w-auto text-center whitespace-nowrap cursor-pointer"
              >
                <i className="ri-shield-star-line text-xl sm:text-2xl shrink-0" />
                <span className="text-sm sm:text-base lg:text-lg leading-tight">
                  Quero reduzir custos e proteger meu patrimônio
                </span>
                <i className="ri-arrow-right-line text-lg sm:text-xl group-hover:translate-x-1 transition-transform shrink-0" />
              </a>
              <p className="text-slate-500 text-xs sm:text-sm mt-3 sm:mt-4 italic flex items-center justify-center sm:justify-start gap-2">
                <i className="ri-shield-star-line text-emerald-500" />
                Resultados percebidos já nas primeiras contratações.
              </p>
            </div>

          </div>

          {/* ── Coluna Direita — Imagem ── */}
          <div className="relative h-full min-h-[400px] sm:min-h-[500px] lg:min-h-[700px] mt-4 lg:mt-0">
            <div className="relative h-full rounded-xl sm:rounded-2xl overflow-hidden">

              {/* Imagem Principal */}
              <img
                src="https://readdy.ai/api/search-image?query=confident%20professional%20insurance%20advisor%20consultant%20shaking%20hands%20with%20satisfied%20corporate%20business%20client%20in%20bright%20modern%20office%20after%20successful%20contract%20signing%20documents%20spread%20on%20elegant%20desk%20both%20smiling%20atmosphere%20of%20trust%20achievement%20and%20strategic%20partnership%20premium%20natural%20lighting%20high%20quality%20editorial%20photography%20clean%20minimal%20corporate%20environment&width=800&height=1000&seq=impacto-ibx-brasil-main-v1&orientation=portrait"
                alt="Consultor IBX Brasil em reunião estratégica com cliente empresarial satisfeito após proteção patrimonial estruturada"
                className="w-full h-full object-cover object-top"
              />

              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/10 to-transparent" />

              {/* Badge superior direito — indicador */}
              <div className="absolute top-4 sm:top-6 right-4 sm:right-6 bg-gradient-to-br from-emerald-600 to-teal-600 text-white rounded-lg sm:rounded-xl px-4 sm:px-5 py-3 sm:py-4 border border-emerald-500/50">
                <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
                  <i className="ri-shield-star-fill text-base sm:text-lg" />
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider opacity-90">
                    Proteção real
                  </span>
                </div>
                <div className="text-xl sm:text-2xl lg:text-3xl font-bold leading-none">+98%</div>
                <div className="text-[10px] sm:text-xs font-medium opacity-90 whitespace-nowrap">
                  clientes satisfeitos
                </div>
              </div>

              {/* Badge lateral esquerdo */}
              <div className="absolute top-1/2 -translate-y-1/2 left-4 sm:left-6 bg-white rounded-lg sm:rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 border border-slate-200 max-w-[150px] sm:max-w-none">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 flex items-center justify-center">
                    <i className="ri-arrow-down-circle-fill text-emerald-600 text-xl sm:text-2xl shrink-0" />
                  </div>
                  <div>
                    <div className="text-emerald-700 font-bold text-base sm:text-lg leading-none">até 35%</div>
                    <div className="text-slate-600 text-[10px] sm:text-xs font-medium whitespace-nowrap">
                      redução de custos
                    </div>
                  </div>
                </div>
              </div>

              {/* Card Flutuante Inferior */}
              <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 right-4 sm:right-6 bg-white rounded-lg sm:rounded-xl p-4 sm:p-6 border border-slate-200">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-lg flex items-center justify-center">
                    <i className="ri-verified-badge-fill text-white text-xl sm:text-2xl" />
                  </div>
                  <div>
                    <h4 className="text-slate-900 font-bold text-base sm:text-lg mb-0.5 sm:mb-1">
                      Método IBX Brasil
                    </h4>
                    <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                      Proteção estratégica estruturada do início ao fim
                    </p>
                  </div>
                </div>

                {/* Mini indicadores */}
                <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-slate-100">
                  {[
                    { icon: 'ri-focus-3-line', label: 'Diagnóstico' },
                    { icon: 'ri-shield-check-line', label: 'Sem falhas' },
                    { icon: 'ri-line-chart-line', label: 'Monitoramento' },
                  ].map((b, idx) => (
                    <div key={idx} className="text-center">
                      <div className="w-full flex items-center justify-center mb-1">
                        <i className={`${b.icon} text-emerald-600 text-base sm:text-lg`} />
                      </div>
                      <span className="text-[9px] sm:text-[10px] text-slate-600 font-medium uppercase tracking-wide leading-tight block">
                        {b.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
          {/* ── Fim Coluna Direita ── */}

        </div>
      </div>
    </section>
  );
}
